// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';

import { api } from '../../../api/client';
import type { SurveyPart, SurveyType, SurveyTypePatch, Treatment } from '../../../api/types';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { useCaseShell } from '../../../app/bygning/CaseShellContext';
import type { CaseActions, CaseData } from '../../../app/bygning/useCaseData';
import { useFieldView } from '../../../app/bygning/useFieldView';
import { createOnceGuard } from '../../../app/onceGuard';
import { errorMessage, saveErrorMessage } from '../../../app/saveError';
import { useMutationQueue } from '../../../app/useMutationQueue';
import {
  allExpanded,
  foldAll,
  foldoutKey,
  moreLabel,
  partPlace,
  readExpanded,
  rowKeyId,
  shownCount,
  toggleExpanded,
  visibleRowKeys,
  writeExpanded,
  FOLDOUT_PAGE,
  type RowKey,
  type SubRow,
} from '../../../bygning/foldout';
import { viewColumns } from '../../../bygning/fields';
import { formatFieldView } from '../../../bygning/fieldView';
import { lensToken } from '../../../bygning/lens';
import { geomTag, partMassText } from '../../../bygning/measure';
import { planToast } from '../../../bygning/parts';
import { isScanPart } from '../../../bygning/provenance';
import { activeFilterCount, massText, roomFigure, type FilteredResources } from '../../../bygning/resourceFilter';
import { reviewPill } from '../../../bygning/review';
import { NO_FILTER_PATCH, type Selection } from '../../../bygning/selection';
import {
  columnCreateBody,
  columnCreateConflict,
  columnPartialFailureMessage,
  duplicateFirst,
  type ColumnDraft,
} from '../../../kortlaegning/columnDraft';
import { cellModel, partDesignation, resourceColumnKeyId, resourceIndex } from '../../../kortlaegning/resources';
import { approveBlocked } from '../../../kortlaegning/samples';
import { appendKeyMember } from '../../../kortlaegning/templatePick';
import { rowThumbView } from '../../../kortlaegning/view';
import { invalidValueMessage } from '../../../kortlaegning/writeOutcome';
import { confidencePercent, formatNumber, formatQuantity, formatTonnes } from '../../../kortlaegning/vocab';
import { Button } from '../../Button';
import { Checkbox } from '../../Checkbox';
import { IconButton } from '../../IconButton';
import { DownloadIcon, Menu, type MenuItem } from '../../Menu';
import { Pill } from '../../Pill';
import { TextLink } from '../../TextLink';
import { AddColumnDialog } from '../../kortlaegning/AddColumnDialog';
import { ResourceCell } from '../../kortlaegning/ResourceCell';
import { TypeMark } from '../../kortlaegning/TypeMark';
import { FieldViewSelect } from '../FieldViewSelect';
import { Portal } from '../inspector/Portal';
import { useStoreys } from '../settings/SettingsContext';
import toolbar from '../toolbar.module.css';
import { BulkBar } from './BulkBar';
import { LazyRowThumb } from './LazyRowThumb';
import { RowThumb } from './RowThumb';
import styles from './Tabel.module.css';

export interface TypesTableProps {
  data: CaseData;
  sel: Selection;
  setSel: (patch: Partial<Selection>) => void;
  actions: CaseActions;
  /** The filtered set (`filterResources`): its types are the rows. */
  filtered: FilteredResources;
  /** The Typer/Dele toggle and the shared filters (TabelTile), first in the toolbar. */
  switcher?: ReactNode;
}

/** The header's fold-all: two chevrons, pointing up once every type is open. */
function ChevronsIcon({ up }: { up: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d={up ? 'M4 11.5l4-4 4 4M4 6.5l4-4 4 4' : 'M4 4.5l4 4 4-4M4 9.5l4 4 4-4'} />
    </svg>
  );
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true" focusable="false">
      <path d="M8 3.5v9M3.5 8h9" />
    </svg>
  );
}

/**
 * Tabel › Typer (v8 table; the old SurveyTable's type rows): checkbox
 * multiselect with the bulk bar, columns from the Felter picker's view (a
 * template, or Alle felter — `useFieldView`, shared with the inspector —
 * plus the report's columns it leaves out, `viewColumns`) with inline
 * edits on the selected row (plan R2), the rows the shared filters leave
 * (`filtered`: room, search, miljøstatus, ★, Fejldetektion — the switcher
 * carries the controls), and Tilføj kolonne and the CSV export behind the
 * toolbar's ⋯. A row click selects the type for the inspector.
 *
 * Each type folds out (frontend-r22.24, `bygning/foldout`) to its parts in
 * the filtered set as sub-rows — code, room · storey, mængde with how it was measured, masse,
 * the part's own resource view, and (with Fejldetektion) its parts marked as
 * an error with Gendan — 50 at a time behind "Vis flere". A sub-row click focuses the
 * part as Tabel › Dele does. ↑/↓ walk the visible rows, →/← fold; the open
 * types are remembered for the session. The checkboxes stay type picks.
 */
export function TypesTable({ data, sel, setSel, actions, filtered, switcher }: TypesTableProps) {
  const shell = useCaseShell();
  const canEdit = useCanEdit();
  const { busy } = data;
  const tableRef = useRef<HTMLDivElement>(null);
  const identity = { id: data.project?.id ?? null, name: shell.projectName ?? '' };

  const fields = useFieldView(data, sel, setSel);
  const { view, template } = fields;
  const columns = useMemo(
    () => viewColumns(view, data.templates, data.resourceKeys),
    [view, data.templates, data.resourceKeys],
  );
  const index = useMemo(() => resourceIndex(data.resources), [data.resources]);
  const storeys = useStoreys();

  const rows: SurveyType[] = useMemo(() => filtered.types.map((m) => m.type), [filtered.types]);

  // ------------------------------------------------------------- fold-out --

  const foldKey = identity.id === null ? identity.name : `id:${identity.id}@${identity.name}`;
  const [expanded, setExpandedState] = useState<ReadonlySet<number>>(() => readExpanded(foldKey));
  const foldKeyRef = useRef(foldKey);
  useEffect(() => {
    if (foldKeyRef.current === foldKey) return;
    foldKeyRef.current = foldKey;
    setExpandedState(readExpanded(foldKey));
  }, [foldKey]);
  // Written on a user's fold only, so a project still loading never overwrites the stored state.
  const setExpanded = (next: ReadonlySet<number>) => {
    setExpandedState(next);
    writeExpanded(foldKeyRef.current, next);
  };
  const [limits, setLimits] = useState<ReadonlyMap<number, number>>(() => new Map());
  // A type folds out to the set's parts of it (room, search, ★ — by code), then its parts
  // marked as an error under the same filters (only with Fejldetektion on, as Dele lists them).
  const subRows = useMemo(() => {
    const out = new Map<number, SubRow[]>();
    for (const m of filtered.types) out.set(m.type.id, m.parts.map((part) => ({ part, dismissed: false })));
    for (const d of filtered.dismissed) out.get(d.type.id)?.push({ part: d.part, dismissed: true });
    return out;
  }, [filtered]);
  const subOf = (t: SurveyType) => subRows.get(t.id) ?? [];
  const hasParts = (id: number) => (subRows.get(id)?.length ?? 0) > 0;
  const foldable = rows.filter((t) => hasParts(t.id)).map((t) => t.id);
  const visible = visibleRowKeys(rows, expanded, subOf, limits);
  const [focusId, setFocusId] = useState<string | null>(null);
  const tabStop = focusId !== null && visible.some((k) => rowKeyId(k) === focusId) ? focusId : (visible[0] ? rowKeyId(visible[0]) : null);
  const pendingFocus = useRef<string | null>(null);
  useEffect(() => {
    const id = pendingFocus.current;
    if (id === null) return;
    const el = tableRef.current?.querySelector<HTMLElement>(`tr[data-row="${CSS.escape(id)}"]`);
    if (!el) return;
    pendingFocus.current = null;
    el.focus();
  });

  /** Focus a row and select what it shows: the type, or the (live) part. */
  function goTo(k: RowKey) {
    const id = rowKeyId(k);
    setFocusId(id);
    pendingFocus.current = id;
    if (k.kind === 'type') setSel({ typeId: k.typeId, partCode: null });
    else if (!subRows.get(k.typeId)?.find((r) => r.part.code === k.code)?.dismissed) {
      setSel({ typeId: k.typeId, partCode: k.code, selParts: sel.selParts });
    }
  }

  function onRowKey(e: KeyboardEvent<HTMLTableSectionElement>) {
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    const target = e.target as HTMLElement;
    if (target.closest('input, select, textarea, [contenteditable="true"]')) return;
    const id = target.closest<HTMLElement>('tr[data-row]')?.dataset.row;
    const at = id === undefined ? undefined : visible.find((k) => rowKeyId(k) === id);
    if (!at) return;
    const move = foldoutKey(e.key, at, visible, expanded, hasParts);
    if (!move) return;
    // Ours, not the shell's (←/→ would otherwise step the strip).
    e.preventDefault();
    e.stopPropagation();
    if (move.toggle !== null) setExpanded(toggleExpanded(expanded, move.toggle));
    if (move.focus) goTo(move.focus);
  }

  function restore(code: string) {
    void actions.patchPart(code, { dismissed: false }).then((part) => {
      if (part) actions.notify(planToast('restore', 1));
    });
  }

  const [checked, setChecked] = useState<ReadonlySet<number>>(() => new Set());
  // A type that left the table (deleted, filtered away) leaves the bulk selection too.
  const live = new Set(rows.map((t) => t.id));
  const picked = [...checked].filter((id) => live.has(id));
  const pickedTypes = data.types.filter((t) => picked.includes(t.id));
  const approvable = pickedTypes.filter((t) => t.review_status === 'queue' && !approveBlocked(t));
  const allChecked = rows.length > 0 && rows.every((t) => checked.has(t.id));

  const toggle = (id: number) =>
    setChecked((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const [bulkRunning, setBulkRunning] = useState(false);
  /** One batch PATCH for the listed types (`actions.patchTypes`; serial on an older server), one toast. */
  async function bulk(patch: SurveyTypePatch, list: SurveyType[], done: (n: number) => string) {
    if (list.length === 0) return;
    setBulkRunning(true);
    let edited: SurveyType[] | null = null;
    try {
      edited = await actions.patchTypes(
        list.map((t) => t.id),
        patch,
      );
    } finally {
      setBulkRunning(false);
    }
    if (edited && edited.length > 0) actions.notify(done(edited.length));
  }

  const plural = (n: number) => (n === 1 ? '1 type' : `${n} typer`);

  // ------------------------------------------------------- Tilføj kolonne --

  const [addColumnOpen, setAddColumnOpen] = useState(false);
  const [columnError, setColumnError] = useState<string | null>(null);
  const columnGuard = useRef(createOnceGuard());
  const { mutate } = useMutationQueue({ onError: (cause) => actions.notify(saveErrorMessage(cause)) });

  /** As the old page: create the column, append it to the template (or a copy of a seed), re-read. */
  function addColumn(draft: ColumnDraft, copyInstead: boolean) {
    const base = template;
    if (!base) return;
    setColumnError(null);
    columnGuard.current.run(() =>
      mutate(async () => {
        let def;
        try {
          def = await api.createResourceColumn(columnCreateBody(draft));
        } catch (cause) {
          const conflict = columnCreateConflict(cause);
          if (conflict === null) throw cause;
          setColumnError(conflict);
          return;
        }
        try {
          const target = duplicateFirst(base, copyInstead) ? await api.duplicateTemplate(base.id) : base;
          await api.patchTemplate(target.id, { members: appendKeyMember(target.members, resourceColumnKeyId(def.id)) });
          fields.choose(formatFieldView({ kind: 'template', id: target.id }));
          actions.notify(`Kolonnen »${def.name}« er tilføjet til »${target.name}«`);
        } catch (cause) {
          actions.notify(columnPartialFailureMessage(def.name, errorMessage(cause)));
        }
        setAddColumnOpen(false);
        actions.refresh();
      }),
    );
  }

  const onInvalid = (label: string) => actions.notify(invalidValueMessage(label));

  const hasQuantityColumn = columns.some((c) => c.key.id === 'sys:quantity');
  const hasRoomColumn = columns.some((c) => c.key.id === 'sys:room');

  function quantityText(part: SurveyPart, type: SurveyType) {
    const g = part.dismissed ? null : geomTag(part, type.unit);
    return (
      <>
        {formatNumber(part.quantity)} {type.unit}
        {g && (
          <span className={g.entered ? `${styles.qtag} ${styles.qtagEntered}` : styles.qtag} title={g.title}>
            {g.label}
          </span>
        )}
      </>
    );
  }

  /** One part of an open type: what Tabel › Dele shows of it, in the type table's columns. */
  function subRow(type: SurveyType, { part, dismissed }: SubRow) {
    const id = rowKeyId({ kind: 'part', typeId: type.id, code: part.code });
    const place = partPlace(part, storeys);
    const designation = partDesignation(index.get(part.code), data.resourceKeys);
    const manual = !isScanPart(part);
    const className = dismissed ? `${styles.subRow} ${styles.muted}` : styles.subRow;
    return (
      <tr
        key={id}
        data-row={id}
        className={className}
        tabIndex={tabStop === id ? 0 : -1}
        aria-level={2}
        aria-selected={!dismissed && sel.partCode === part.code}
        aria-label={dismissed ? `${part.code} — markeret som fejldetektion` : undefined}
        onClick={dismissed ? undefined : () => setSel({ typeId: type.id, partCode: part.code, selParts: sel.selParts })}
      >
        <td className={styles.cellCheck} />
        <td className={styles.cellToggle}>
          <span className={styles.subGuide} aria-hidden="true" />
        </td>
        <td className={styles.mono}>
          <span className={styles.name}>
            <LazyRowThumb view={index.get(part.code)?.view ?? null} />
            <span>
              {part.starred && (
                <span className={styles.star} role="img" aria-label="Vigtig">
                  ★{' '}
                </span>
              )}
              {part.code}
              {designation && <span className={styles.muted}> · {designation}</span>}
            </span>
            {!hasRoomColumn && place && <span className={styles.subPlace}>{place}</span>}
            {!hasQuantityColumn && <span className={styles.subPlace}>{quantityText(part, type)}</span>}
          </span>
        </td>
        {columns.map((column) => {
          if (column.key.id === 'sys:quantity') {
            return (
              <td key={column.key.id} className={styles.num}>
                {quantityText(part, type)}
              </td>
            );
          }
          if (column.key.id === 'sys:room') return <td key={column.key.id}>{place || <span className={styles.muted}>–</span>}</td>;
          // A type-scoped value is the type's, on its row.
          if (column.typeScoped) return <td key={column.key.id} />;
          const m = cellModel({ kind: 'part', typeId: type.id, partCode: part.code }, type, column, index);
          return (
            <td key={column.key.id}>
              <ResourceCell resourceKey={column.key} value={m.value} editing={false} onCommit={() => undefined} onInvalid={onInvalid} home={tableRef} />
            </td>
          );
        })}
        <td className={styles.num} title={part.mass_method ?? undefined}>
          {dismissed ? null : (partMassText(part) ?? <span className={styles.muted}>–</span>)}
        </td>
        <td />
        <td>
          <span className={styles.name}>
            {dismissed ? (
              <>
                <Pill variant="outline">Fejldetektion</Pill>
                {canEdit && (
                  <Button disabled={busy} aria-label={`Gendan ${part.code}`} title="Delen tæller med igen" onClick={() => restore(part.code)}>
                    Gendan
                  </Button>
                )}
              </>
            ) : (
              <>
                {manual && <Pill variant="outline">Manuel</Pill>}
                {part.orphaned && (
                  <Pill tone="warn" title="Instansen findes ikke længere — gennemgå eller flyt delen">
                    forældet
                  </Pill>
                )}
                {part.note && (
                  <span className={styles.muted} role="img" aria-label={`Har note: ${part.note}`} title={part.note}>
                    ✎
                  </span>
                )}
              </>
            )}
          </span>
        </td>
      </tr>
    );
  }

  // The toolbar's ⋯: Tilføj kolonne (editors), the CSV export (once there are types).
  const menuItems: MenuItem[] = [
    ...(canEdit
      ? [
          {
            id: 'add-column',
            label: 'Tilføj kolonne',
            icon: <PlusIcon />,
            disabled: template === null,
            title: template === null ? 'Vælg en skabelon først' : undefined,
            onSelect: () => {
              setColumnError(null);
              setAddColumnOpen(true);
            },
          },
        ]
      : []),
    ...(data.types.length > 0
      ? [{ id: 'export-csv', label: 'Eksportér CSV', icon: <DownloadIcon />, href: api.csvExportUrl(), download: true }]
      : []),
  ];
  const everyOpen = allExpanded(expanded, foldable);

  return (
    <>
      <div className={toolbar.tools}>
        <div className={toolbar.toolsLead}>
          {switcher}
        </div>
        <div className={toolbar.toolsEnd}>
          <FieldViewSelect
            id="tabel-felter"
            label="Felter"
            options={fields.options}
            value={formatFieldView(view)}
            onChange={fields.choose}
            variant="filled"
            size="md"
          />
          {menuItems.length > 0 && <Menu label="Flere handlinger" items={menuItems} />}
        </div>
      </div>

      <div ref={tableRef} className={styles.wrap} tabIndex={-1}>
        <table className={styles.table} role="treegrid" aria-label="Typer">
          <thead>
            <tr>
              <th className={styles.cellCheck}>
                <Checkbox
                  aria-label="Vælg alle typer"
                  checked={allChecked}
                  onChange={() => setChecked(allChecked ? new Set() : new Set(rows.map((t) => t.id)))}
                />
              </th>
              <th className={styles.cellToggle}>
                {foldable.length > 0 ? (
                  <IconButton
                    variant="ghost"
                    size="sm"
                    className={styles.foldAll}
                    label={everyOpen ? 'Fold alle ind' : 'Fold alle ud'}
                    onClick={() => setExpanded(foldAll(expanded, foldable))}
                  >
                    <ChevronsIcon up={everyOpen} />
                  </IconButton>
                ) : (
                  <span className={styles.srOnly}>Fold ud</span>
                )}
              </th>
              <th>Type</th>
              {columns.map((c) => (
                <th key={c.key.id}>
                  {c.key.label}
                  {c.typeScoped && <TypeMark />}
                </th>
              ))}
              <th className={styles.num}>Masse</th>
              <th className={styles.num}>Sikkerhed</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody onKeyDown={onRowKey} onFocus={(e) => {
            const id = (e.target as HTMLElement).closest<HTMLElement>('tr[data-row]')?.dataset.row;
            if (id !== undefined) setFocusId(id);
          }}>
            {rows.length === 0 ? (
              <tr>
                <td className={styles.empty} colSpan={columns.length + 6}>
                  {data.types.length === 0 ? (
                    'Ingen typer endnu.'
                  ) : (
                    <>
                      Ingen typer matcher filtrene.{' '}
                      {activeFilterCount(sel) > 0 && <TextLink onClick={() => setSel(NO_FILTER_PATCH)}>Nulstil filtre</TextLink>}
                    </>
                  )}
                </td>
              </tr>
            ) : (
              rows.flatMap((t) => {
                const selected = sel.typeId === t.id;
                const open = expanded.has(t.id);
                const subs = subOf(t);
                const partShown = selected && open && sel.partCode !== null && subs.some((r) => r.part.code === sel.partCode);
                const rowId = rowKeyId({ kind: 'type', typeId: t.id });
                const token = lensToken(t, sel.lens);
                const pill = reviewPill(t);
                const conf = confidencePercent(t.confidence);
                // Under a room focus the row's parts, mængde and masse are the room's share (as the strip and Galleri).
                const fig = roomFigure(t, sel.room);
                const typeRow = (
                  <tr
                    key={t.id}
                    data-row={rowId}
                    tabIndex={tabStop === rowId ? 0 : -1}
                    aria-level={1}
                    aria-expanded={subs.length > 0 ? open : undefined}
                    aria-selected={selected && !partShown}
                    onClick={() => setSel({ typeId: t.id })}
                  >
                    <td className={styles.cellCheck} onClick={(e) => e.stopPropagation()}>
                      <Checkbox aria-label={`Vælg ${t.name}`} checked={checked.has(t.id)} onChange={() => toggle(t.id)} />
                    </td>
                    <td className={styles.cellToggle} onClick={(e) => e.stopPropagation()}>
                      {subs.length > 0 && (
                        <button
                          type="button"
                          className={styles.disclosure}
                          aria-expanded={open}
                          aria-label={open ? `Fold ${t.name} ind` : `Fold ${t.name} ud`}
                          tabIndex={-1}
                          onClick={() => setExpanded(toggleExpanded(expanded, t.id))}
                        >
                          <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">
                            <path d="M6 3.5 10.5 8 6 12.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      )}
                    </td>
                    <td>
                      <span className={styles.name}>
                        <i
                          className={token ? styles.swatch : `${styles.swatch} ${styles.ghost}`}
                          style={token ? { background: `var(${token})` } : undefined}
                          aria-hidden="true"
                        />
                        <RowThumb view={rowThumbView(t.parts, index)} />
                        {t.starred && (
                          <span className={styles.star} role="img" aria-label="Vigtig">
                            ★
                          </span>
                        )}
                        {t.name}
                        <span className={styles.muted}>{fig.parts === 1 ? '1 del' : `${fig.parts} dele`}</span>
                      </span>
                    </td>
                    {columns.map((column) => {
                      const m = cellModel({ kind: 'type', typeId: t.id }, t, column, index);
                      if (m.kind === 'aggregate') {
                        return (
                          <td key={column.key.id} className={styles.num}>
                            {formatQuantity(fig.quantity, t.unit)}
                          </td>
                        );
                      }
                      if (m.kind === 'none') return <td key={column.key.id} />;
                      const target = m.target;
                      return (
                        <td key={column.key.id}>
                          <ResourceCell
                            resourceKey={column.key}
                            value={m.value}
                            editing={canEdit && selected && target !== null}
                            onCommit={(v) =>
                              target !== null ? actions.patchResource(target, { [column.key.id]: v }, column.key.label).then(() => undefined) : undefined
                            }
                            onInvalid={onInvalid}
                            home={tableRef}
                          />
                        </td>
                      );
                    })}
                    <td className={styles.num}>
                      {fig.mass === null ? <Pill tone="warn">mangler</Pill> : sel.room === null ? formatTonnes(fig.mass) : massText(fig)}
                    </td>
                    <td className={`${styles.num} ${styles.muted}`}>{conf === null ? '–' : `${conf} %`}</td>
                    <td>
                      <Pill tone={pill.tone}>{pill.label}</Pill>
                    </td>
                  </tr>
                );
                if (!open) return [typeRow];
                const n = shownCount(subs.length, limits.get(t.id));
                const more = moreLabel(subs.length, n);
                const out = [typeRow, ...subs.slice(0, n).map((r) => subRow(t, r))];
                if (more) {
                  out.push(
                    <tr key={`more:${t.id}`} className={styles.subMore}>
                      <td colSpan={2} />
                      <td colSpan={columns.length + 4}>
                        <TextLink size="sm" onClick={() => setLimits(new Map(limits).set(t.id, n + FOLDOUT_PAGE))}>
                          {more}
                        </TextLink>
                      </td>
                    </tr>,
                  );
                }
                return out;
              })
            )}
          </tbody>
        </table>
        {picked.length > 0 && (
          <BulkBar
            count={picked.length}
            approvable={approvable.length}
            canEdit={canEdit}
            disabled={busy || bulkRunning}
            onTreatment={(treatment: Treatment) =>
              void bulk({ treatment }, pickedTypes, (n) => `Behandling sat på ${plural(n)}`)
            }
            onEak={(code) => void bulk({ eak_code: code }, pickedTypes, (n) => `EAK sat på ${plural(n)}`)}
            onApprove={() =>
              void bulk({ review_status: 'approved' }, approvable, (n) => `${plural(n)} godkendt`).then(() => setChecked(new Set()))
            }
            onClear={() => setChecked(new Set())}
          />
        )}
      </div>

      {canEdit && addColumnOpen && (
        <Portal>
          <AddColumnDialog
            template={template}
            existingLabels={data.resourceKeys.map((k) => k.label)}
            busy={busy}
            serverError={columnError}
            onCancel={() => setAddColumnOpen(false)}
            onSubmit={addColumn}
          />
        </Portal>
      )}
    </>
  );
}
